import { DeleteOutlined } from '@ant-design/icons'
import { LocationOn, MoreHoriz } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { PlaceNode } from '@ulysses-inc/harami_api_client'
import { type MouseEvent, useState } from 'react'
import Loading from '../../components/loading/Loading'
import { gray } from '../muikds/kdsColors'
import { semanticColors } from '../theme/semanticColors'

interface Props {
  places: PlaceNode[]
  isLoading: boolean
  openEditPlace: (place: PlaceNode) => void
  deletePlace: (id: string) => void
}

interface RowData {
  uuid: string
  name?: string
}

const PlaceList = (props: Props) => {
  // メニュー
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null)
  const handleMenuClick = (event: MouseEvent<HTMLElement>, row: RowData) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedRow(row)
  }
  const handleMenuClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  // 削除ダイアログ
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const handleClickOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setOpenDeleteDialog(true)
  }
  const handleCloseDeleteDialog = (event: MouseEvent<HTMLElement>) => {
    setOpenDeleteDialog(false)
    handleMenuClose(event)
  }
  const handleDelete = (
    event: MouseEvent<HTMLElement>,
    uuid: string | undefined,
  ) => {
    if (uuid !== undefined) {
      props.deletePlace(uuid)
    }
    setOpenDeleteDialog(false)
    handleMenuClose(event)
  }

  if (props.isLoading) {
    return <Loading />
  }

  return (
    <>
      <TableHead sx={{ backgroundColor: 'white' }}>
        <TableRow>
          <TableCell
            size={'small'}
            sx={{
              whiteSpace: 'normal',
              backgroundColor: 'white',
            }}
          >
            現場名
          </TableCell>
          <TableCell
            size={'small'}
            sx={{
              whiteSpace: 'normal',
              backgroundColor: 'white',
            }}
          ></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {props.places.map((node: PlaceNode) => (
          <TableRow
            onClick={() => props.openEditPlace(node)}
            key={node.uuid}
            sx={{
              cursor: 'pointer',
              '&:hover': { backgroundColor: semanticColors.colorBgTextHover },
            }}
          >
            <TableCell
              size={'small'}
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              <Typography
                variant="body2"
                display="flex"
                alignItems="center"
                sx={{ gap: 1 }}
              >
                <LocationOn fontSize="small" sx={{ color: gray[7] }} />
                {node.place?.name}
              </Typography>
            </TableCell>
            <TableCell size={'small'} sx={{ minWidth: '96px' }} align="right">
              <IconButton
                onClick={e => {
                  handleMenuClick(e, {
                    uuid: node.uuid,
                    name: node.place?.name,
                  })
                }}
              >
                <MoreHoriz />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={handleClickOpen}
          sx={{
            color: semanticColors.colorErrorText,
            '&:hover': {
              backgroundColor: semanticColors.colorErrorBgHover,
              color: semanticColors.colorErrorText,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{`「${selectedRow?.name}」の削除を実行しますがよろしいですか？`}</DialogTitle>
        <DialogActions>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleCloseDeleteDialog}
          >
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={e => handleDelete(e, selectedRow?.uuid)}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PlaceList
